import { Initiative } from '@schema/initiative'
import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { EditContainer } from './blocks/shared/EditContainer'
import { MainDetailsImagesEdit } from './blocks/mainDetails/MainDetailsImagesEdit'
import { MainDetailsVisionEdit } from './blocks/mainDetails/MainDetailsVisionEdit'
import { NameAndSloganEdit } from './blocks/mainDetails/NameAndSloganEdit'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import Row from '@app/components/layout/Row'
import { PreviewImage } from '@app/components/core/image/PreviewImage'

export type MainDetailsEditProps = {
  initiative: Partial<Initiative>
}

export const MainDetailsEdit = ({ initiative }: MainDetailsEditProps) => {
  const translationsRoot = useTranslations().app
  const creationTranslations = translationsRoot.dashboard.initiative.creation
  const viewTranslations = translationsRoot.dashboard.initiative.view

  if (!initiative.id) {
    return null
  }

  const id = initiative.id
  const title = initiative.title ?? ''
  const slogan = initiative.description ?? ''
  const vision = initiative.vision ?? ''
  const images = initiative.images ?? []

  const nameDisplay =
    title.trim() !== '' ? title : viewTranslations.nameEmptyLabel
  const sloganDisplay =
    slogan.trim() !== '' ? slogan : viewTranslations.sloganEmptyLabel

  return (
    <Column alignItems="stretch" style={{ flexGrow: 0 }} gap="2rem">
      <EditContainer>
        <EditContainer.View>
          <Column gap="0.5rem" alignItems="stretch">
            <Title.H3>{nameDisplay}</Title.H3>
            <Text.Medium color={'var(--palette-blue-500)'}>
              {sloganDisplay}
            </Text.Medium>
          </Column>
        </EditContainer.View>
        <EditContainer.Edit>
          <NameAndSloganEdit initiativeId={id} title={title} slogan={slogan} />
        </EditContainer.Edit>
      </EditContainer>

      <EditContainer>
        <EditContainer.View>
          {vision.trim() !== '' ? (
            <Text.Medium
              color={'var(--palette-blue-500)'}
              style={{ whiteSpace: 'pre-line' }}
            >
              <ParagraphedText>{vision}</ParagraphedText>
            </Text.Medium>
          ) : (
            <Text.Medium color={'var(--palette-blue-500)'}>
              {viewTranslations.visionEmptyLabel}
            </Text.Medium>
          )}
        </EditContainer.View>
        <EditContainer.Edit>
          <MainDetailsVisionEdit initiativeId={id} vision={vision} />
        </EditContainer.Edit>
      </EditContainer>

      <EditContainer title={creationTranslations.imagesLabel}>
        <EditContainer.View>
          {images.length > 0 ? (
            <Row style={{ flexWrap: 'wrap' }}>
              {images.map((image, i) => (
                <PreviewImage key={i} src={image.src} />
              ))}
            </Row>
          ) : (
            <Text.Medium color={'var(--palette-blue-500)'}>
              {viewTranslations.photosEmptyLabel}
            </Text.Medium>
          )}
        </EditContainer.View>
        <EditContainer.Edit>
          <MainDetailsImagesEdit
            initiativeId={id}
            existingImages={images.map((img) => ({
              id: img.id,
              src: img.src,
            }))}
          />
        </EditContainer.Edit>
      </EditContainer>
    </Column>
  )
}
